import { Button as ButtonPrimitive } from '@base-ui/react';
import { cva, cx, type VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';

import { Spinner } from '../spinner';
import styles from './button.module.css';

const button = cva(styles['button'], {
  variants: {
    variant: {
      solid: styles['button-solid'],
      outline: styles['button-outline'],
      ghost: styles['button-ghost'],
      text: styles['button-text']
    },
    size: {
      small: styles['button-small'],
      normal: styles['button-normal']
    },
    disabled: {
      true: styles['button-disabled']
    },
    loading: {
      true: styles['button-loading']
    },
    color: {
      accent: styles['button-color-accent'],
      danger: styles['button-color-danger'],
      neutral: styles['button-color-neutral'],
      success: styles['button-color-success']
    }
  },
  compoundVariants: [
    {
      variant: 'solid',
      color: 'accent',
      className: styles['button-solid-accent']
    },
    {
      variant: 'solid',
      color: 'danger',
      className: styles['button-solid-danger']
    },
    {
      variant: 'solid',
      color: 'neutral',
      className: styles['button-solid-neutral']
    },
    {
      variant: 'solid',
      color: 'success',
      className: styles['button-solid-success']
    },
    {
      variant: 'outline',
      color: 'accent',
      className: styles['button-outline-accent']
    },
    {
      variant: 'outline',
      color: 'danger',
      className: styles['button-outline-danger']
    },
    {
      variant: 'outline',
      color: 'neutral',
      className: styles['button-outline-neutral']
    },
    {
      variant: 'outline',
      color: 'success',
      className: styles['button-outline-success']
    },
    {
      variant: 'ghost',
      color: 'accent',
      className: styles['button-ghost-accent']
    },
    {
      variant: 'ghost',
      color: 'danger',
      className: styles['button-ghost-danger']
    },
    {
      variant: 'ghost',
      color: 'neutral',
      className: styles['button-ghost-neutral']
    },
    {
      variant: 'ghost',
      color: 'success',
      className: styles['button-ghost-success']
    },
    {
      variant: 'text',
      color: 'accent',
      className: styles['button-text-accent']
    },
    {
      variant: 'text',
      color: 'danger',
      className: styles['button-text-danger']
    },
    {
      variant: 'text',
      color: 'neutral',
      className: styles['button-text-neutral']
    },
    {
      variant: 'text',
      color: 'success',
      className: styles['button-text-success']
    }
  ],
  defaultVariants: {
    variant: 'solid',
    size: 'normal',
    color: 'accent'
  }
});

const getLoaderOnlyClass = (size: 'small' | 'normal' | null) =>
  size === 'small'
    ? styles['loader-only-button-small']
    : styles['loader-only-button-normal'];

type ButtonProps = VariantProps<typeof button> &
  ButtonPrimitive.Props & {
    loading?: boolean;
    loaderText?: ReactNode;
    leadingIcon?: ReactNode;
    trailingIcon?: ReactNode;
    style?: React.CSSProperties;
  };

export const Button = ({
  className,
  variant = 'solid',
  color = 'accent',
  size = 'normal',
  disabled,
  loading,
  loaderText,
  leadingIcon,
  trailingIcon,
  children,
  render,
  ...props
}: ButtonProps) => {
  const isLoaderOnly = loading && !loaderText;

  return (
    <ButtonPrimitive
      className={cx(
        button({ variant, size, color, disabled, loading, className }),
        isLoaderOnly && getLoaderOnlyClass(size)
      )}
      disabled={disabled}
      render={render}
      nativeButton={!render}
      focusableWhenDisabled={loading}
      aria-busy={loading || undefined}
      data-slot='button'
      {...props}
    >
      {loading ? (
        <>
          <Spinner
            size={1}
            color='default'
            aria-hidden='true'
            data-slot='button-loader'
          />
          {loaderText && (
            <span
              className={styles['loader-text']}
              data-slot='button-loader-text'
            >
              {loaderText}
            </span>
          )}
        </>
      ) : (
        <>
          {leadingIcon && (
            <span
              className={cx(styles['icon'], styles['icon-leading'])}
              data-slot='button-leading-icon'
            >
              {leadingIcon}
            </span>
          )}
          {children}
          {trailingIcon && (
            <span
              className={cx(styles['icon'], styles['icon-trailing'])}
              data-slot='button-trailing-icon'
            >
              {trailingIcon}
            </span>
          )}
        </>
      )}
    </ButtonPrimitive>
  );
};
Button.displayName = 'Button';
